import type { ErrorInfo } from "react";
import { captureException, captureMessage, withScope } from "@sentry/react";

/**
 * Reports an error caught by a `react-error-boundary` boundary to Sentry.
 *
 * Unlike `@sentry/react`'s own `Sentry.ErrorBoundary`, `react-error-boundary`
 * does NOT report to Sentry. Boundary-caught render errors therefore only ever
 * surfaced as console Logs (via `consoleLoggingIntegration`), never as Issues.
 * Wiring this as each boundary's `onError` captures them as exceptions.
 *
 * All Sentry calls are safe no-ops when the SDK is not initialised
 * (localdev / MSW / tests).
 */
export function reportError(error: unknown, info?: ErrorInfo): void {
  withScope((scope) => {
    if (info?.componentStack) {
      scope.setExtra("componentStack", info.componentStack);
    }

    if (error instanceof Error) {
      captureException(error);
    } else if (typeof error === "string") {
      captureMessage(error, "error");
    } else {
      captureException(new Error(`Non-error thrown: ${JSON.stringify(error)}`));
    }
  });
}

export default reportError;
